import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../hooks/use-toast";
import api from "../api/client";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Verify token
    api.get(`/auth/verify-reset-token/${token}`)
      .then(res => {
        setValidToken(true);
        setEmail(res.data.email);
      })
      .catch(() => {
        setValidToken(false);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast({ variant: "destructive", title: "Erreur", description: "Les mots de passe ne correspondent pas" });
      return;
    }

    if (password.length < 8) {
      toast({ variant: "destructive", title: "Erreur", description: "Le mot de passe doit contenir au moins 8 caractères" });
      return;
    }

    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast({ title: "Succès", description: "Ton mot de passe a été mis à jour !" });
      navigate("/login");
    } catch (err) {
      toast({ 
        variant: "destructive", 
        title: "Erreur", 
        description: err.response?.data?.detail || "Une erreur est survenue" 
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #060912 100%)" }}>
        <div className="text-white">Vérification en cours...</div>
      </div>
    );
  }

  if (!token || !validToken) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #060912 100%)" }}>
        <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-white mb-2">Lien invalide ou expiré</h2>
          <p className="text-slate-400 mb-6">Ce lien de réinitialisation n'est plus valide. Il a peut-être expiré ou a déjà été utilisé.</p>
          <button
            onClick={() => navigate("/login")}
            className="btn-primary w-full py-3"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #060912 100%)" }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <img src="/logo-boussole.png" alt="Odys'Mentoring" className="w-12 h-12" />
            <span className="font-bold text-xl" style={{ fontFamily: "Outfit, sans-serif" }}>
              <span className="text-white">Odys'</span><span className="text-amber-400">Mentoring</span>
            </span>
          </div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
            Nouveau mot de passe
          </h2>
          <p className="text-slate-500 mt-2 text-sm">
            Crée un nouveau mot de passe pour <span className="text-cyan-400">{email}</span>
          </p>
        </div>

        <div className="glass-card rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-400 block mb-2">Nouveau mot de passe</label>
              <input
                type="password"
                required
                className="input-dark w-full"
                placeholder="••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                minLength={8}
              />
              <p className="text-xs text-slate-500 mt-1">Minimum 8 caractères</p>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-400 block mb-2">Confirmer le mot de passe</label>
              <input
                type="password"
                required
                className="input-dark w-full"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                minLength={8}
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full py-3.5 text-sm mt-6"
            >
              {submitting ? "Mise à jour..." : "Mettre à jour mon mot de passe"}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate("/login")}
            className="text-sm text-slate-500 hover:text-slate-400 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
}
